<template>
  <div id="tabletDashboard">
    <div class="main">
      <div class="title_bar">
        <div class="left">
          <div class="title">
            {{ $t('Household.Dashboard.Title') }}
          </div>
          <div class="tip">
            {{ $t('Household.Dashboard.Description') }}
          </div>
        </div>
      </div>
      <FilterFrame
        ref="filter"
        :save-filter="true"
        :filter-form="tabletFilter"
        @search="search"
        @clear="clearSearch()"
      />
      <div class="center-block">
        <div style="margin-left: auto;">
          <el-button
            round
            @click="exportResidentData"
          >
            {{ $t('Household.Dashboard.ExportResidentData') }}
          </el-button>
        </div>
      </div>
      <el-table
        ref="table"
        v-horizontal-scroll="'always'"
        class="newslist"
        :data="tabletList"
        :empty-text="$t('Household.Dashboard.NoData')"
        row-key="TabletId"
        :indent="50"
        :tree-props="{children: 'children'}"
        @sort-change="onSortChange"
      >
        <!-- 這一個cell只是給上下折疊箭頭留的空間 -->
        <el-table-column
          prop="TabletId"
          label=""
          width="33"
        >
          <template slot-scope="scope">
            <!-- element ui對於沒有子層的欄位不會有icon，我們需求要，所以補上去-->
            <div
              v-if="
                !scope.row.ParentId &&
                  scope.row.children?.length === 0 &&
                  filter.isVirtual !== 1
              "
              class="el-table__expand-icon"
            >
              <i class="el-icon-arrow-right icon-disable" />
            </div>
          </template>
        </el-table-column>

        <!-- # -->
        <el-table-column
          label="#"
          width="50"
        >
          <template slot-scope="scope">
            {{ scope.row.index }}
          </template>
        </el-table-column>

        <!-- 手機開通 -->
        <el-table-column
          prop="HasDeviceBound"
          width="110"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        >
          <template #header>
            {{ $t('Household.Dashboard.TableHeaders.PhoneActivation') }}
          </template>
          <template slot-scope="scope">
            <span :class="scope.row.HasDeviceBound ? 'text-opened' : 'text-notOpened'">
              {{ scope.row.HasDeviceBound ? $t('Household.Dashboard.Status.Activated') : $t('Household.Dashboard.Status.NotActivated') }}
            </span>
          </template>
        </el-table-column>

        <!-- 棟別 -->
        <el-table-column
          prop="Tag"
          :label="$t('Household.Dashboard.TableHeaders.Building')"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        />

        <!-- 戶別 -->
        <el-table-column
          prop="TabletNote"
          :label="$t('Household.Dashboard.TableHeaders.Unit')"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        >
          <template slot-scope="scope">
            <!-- 如果正在編輯新增承租戶，顯示輸入框和按鈕 -->
            <div v-if="scope.row.upsert" class="upsert-virtual-name">
              {{ scope.row.upsert === 'insert' ? scope.row.TabletNote : getBaseTabletNote(scope.row) }}
              <el-input
                v-model="inputVirtualName"
                size="small"
                :class="{invalid: isInvalid}"
                :placeholder="$t('Household.Dashboard.Placeholders.VirtualTabletName')"
                @input="checkIsInvalid"
              />
            </div>
            <!-- 一般顯示戶別名稱 -->
            <div v-else>
              {{ scope.row.TabletNote }}
            </div>
          </template>
        </el-table-column>

        <!-- 戶別類型，有開承租戶功能才顯示 -->
        <el-table-column
          v-if="isVirtualEnabled"
          prop="IsVirtual"
          :label="$t('Household.Dashboard.TableHeaders.UnitType')"
          width="100"
        >
          <template slot-scope="scope">
            {{ scope.row.IsVirtual === 1 ? $t('Household.Dashboard.Status.Tenant') : $t('Household.Dashboard.Status.Owner') }}
          </template>
        </el-table-column>

        <!-- 郵件隱私 -->
        <el-table-column
          prop="PrivacyCount"
          :label="$t('Household.Dashboard.TableHeaders.MailPrivacy')"
          width="110"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        >
          <template slot-scope="scope">
            <span class="privacy-count">
              {{ scope.row.PrivacyCount }}
            </span>
          </template>
        </el-table-column>

        <!-- 開通序號 -->
        <el-table-column
          prop="TabletId"
          width="100"
        >
          <template #header>
            {{ $t('Household.Dashboard.TableHeaders.ActivationCode') }}
          </template>
          <template slot-scope="scope">
            <div>
              <el-button
                size="mini"
                plain
                :disabled="scope.row.upsert === 'insert'"
                @click="generateActivationCode(scope.row.TabletId, scope.row.TabletNote)"
              >
                {{ $t('Household.Dashboard.Buttons.Generate') }}
              </el-button>
            </div>
          </template>
        </el-table-column>

        <!-- 裝置數量 -->
        <el-table-column
          prop="DeviceCount"
          :label="$t('Household.Dashboard.TableHeaders.DeviceCount')"
          width="110"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        />

        <!-- 載具數量 -->
        <el-table-column
          prop="CarrierCount"
          :label="$t('Household.Dashboard.TableHeaders.CarrierCount')"
          width="110"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        />

        <!-- 裝置 / 載具明細 -->
        <el-table-column
          width="100"
        >
          <template #header>
            <span v-for="(line, index) in $t('Household.Dashboard.TableHeaders.DeviceCarrierDetails')" :key="index">
              {{ line }}<br v-if="index < $t('Household.Dashboard.TableHeaders.DeviceCarrierDetails').length - 1">
            </span>
          </template>
          <template slot-scope="scope">
            <el-button
              v-if="!(isVirtualEnabled && scope.row.IsVirtual === 1)"
              size="mini"
              type="primary"
              plain
              :disabled="scope.row.upsert === 'insert'"
              @click="queryDeviceDetails(scope.row.TabletId, scope.row.TabletNote)"
            >
              {{ $t('Household.Dashboard.Buttons.Query') }}
            </el-button>
          </template>
        </el-table-column>

        <!-- 是否租屋 -->
        <el-table-column
          prop="IsRent"
          width="110"
          sortable="custom"
          :sort-orders="['ascending', 'descending']"
        >
          <template #header>
            {{ $t('Household.Dashboard.TableHeaders.IsRent') }}
          </template>
          <template slot-scope="scope">
            <div>
              <div class="is-rent">
                <!-- 社區沒開承租戶或是社區有開承租戶且本身是區權戶顯示「租屋」 -->
                <el-tooltip
                  v-if="!isVirtualEnabled || scope.row.IsVirtual === 0"
                  :content="isUpserting ? $t('Household.Dashboard.Tooltips.CompleteCurrentEdit') : (scope.row.IsRent === 1 ? $t('Household.Dashboard.Tooltips.CancelRentType') : $t('Household.Dashboard.Tooltips.SetAsRental'))"
                  placement="top"
                  effect="dark"
                >
                  <el-button
                    size="mini"
                    :plain="scope.row.IsRent !== 1"
                    :type="scope.row.IsRent === 1 ? 'primary' : 'default'"
                    :disabled="isUpserting"
                    @click="updateTabletIsRent(scope.row)"
                  >
                    {{ $t('Household.Dashboard.Buttons.Rent') }}
                  </el-button>
                </el-tooltip>
                <!-- 社區有開承租戶、本身是區權戶、有開啟租屋功能，則顯示「新增戶別」 -->
                <el-tooltip
                  v-if="isVirtualEnabled && scope.row.IsVirtual === 0 && scope.row.IsRent === 1 && isUpserting"
                  :content="$t('Household.Dashboard.Tooltips.CompleteCurrentEdit')"
                  placement="top"
                  effect="dark"
                >
                  <div>
                    <el-button
                      size="mini"
                      type="warning"
                      :disabled="isUpserting"
                      @click="addVirtual(scope.row)"
                    >
                      {{ $t('Household.Dashboard.Buttons.AddHousehold') }}
                    </el-button>
                  </div>
                </el-tooltip>
                <el-button
                  v-else-if="isVirtualEnabled && scope.row.IsVirtual === 0 && scope.row.IsRent === 1"
                  size="mini"
                  type="warning"
                  :disabled="isUpserting"
                  @click="addVirtual(scope.row)"
                >
                  {{ $t('Household.Dashboard.Buttons.AddHousehold') }}
                </el-button>
                <template v-else>
                  <!-- 承租戶是刪除、確定、取消按鈕 -->
                  <template v-if="scope.row.upsert">
                    <!-- 新增中的承租戶會是確定、取消按鈕 -->
                    <el-button
                      size="mini"
                      type="primary"
                      @click="()=>{
                        if(scope.row.upsert === 'insert'){
                          confirmAddVirtual(scope.row)
                        } else {
                          confirmEditVirtual(scope.row)
                        }
                      }"
                    >
                      {{ $t('General.OK') }}
                    </el-button>
                    <el-button
                      size="mini"
                      @click="() => {
                        if(scope.row.upsert === 'insert'){
                          cancelAddVirtual(scope.row)
                        } else {
                          cancelEditVirtual(scope.row)
                        }
                      }"
                    >
                      {{ $t('General.Cancel') }}
                    </el-button>
                  </template>
                  <template v-else>
                    <!-- 社區有開承租戶，且本身為承租戶，則給編輯、刪除按鈕 -->
                    <template v-if="isVirtualEnabled &&scope.row.IsVirtual === 1">
                      <el-tooltip
                        v-if="isUpserting"
                        :content="$t('Household.Dashboard.Tooltips.CompleteCurrentEdit')"
                        placement="top"
                        effect="dark"
                      >
                        <div>
                          <el-button
                            size="mini"
                            plain
                            :disabled="isUpserting"
                            @click="editVirtual(scope.row)"
                          >
                            {{ $t('General.Edit') }}
                          </el-button>
                        </div>
                      </el-tooltip>
                      <el-button
                        v-else
                        size="mini"
                        plain
                        :disabled="isUpserting"
                        @click="editVirtual(scope.row)"
                      >
                        {{ $t('General.Edit') }}
                      </el-button>
                      <el-tooltip
                        v-if="isUpserting"
                        :content="$t('Household.Dashboard.Tooltips.CompleteCurrentEdit')"
                        placement="top"
                        effect="dark"
                      >
                        <div>
                          <el-button
                            size="mini"
                            type="danger"
                            plain
                            :disabled="isUpserting"
                            @click="deleteVirtual(scope.row)"
                          >
                            {{ $t('General.Delete') }}
                          </el-button>
                        </div>
                      </el-tooltip>
                      <el-button
                        v-else
                        size="mini"
                        type="danger"
                        plain
                        :disabled="isUpserting"
                        @click="deleteVirtual(scope.row)"
                      >
                        {{ $t('General.Delete') }}
                      </el-button>
                    </template>
                  </template>
                </template>
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- 住戶資料 -->
        <el-table-column
          :label="$t('Household.Dashboard.TableHeaders.ResidentData')"
          width="50"
        >
          <template slot-scope="scope">
            <router-link
              :to="scope.row.upsert === 'insert' ? '' : { name: 'householdDetail', params: { id: scope.row.TabletId } }"
              class="detail-link"
              :class="{ 'disabled': scope.row.upsert === 'insert' }"
            >
              {{ $t('Household.Dashboard.Buttons.Details') }}
            </router-link>
          </template>
        </el-table-column>
      </el-table>
      <TableLoading :is-loading="loading" />
    </div>
    <GetActivationCode
      ref="getActivationCode"
    />
    <DeviceRFIDDetailDialog
      ref="deviceRfidDetailDialog"
      @unbind-success="unbindSuccess"
    />
  </div>
</template>

<script>
import TableLoading from '@/components/TableLoading'
import FilterFrame from '@/components/FilterFrame'
import { housekeeperAPI } from '@/api/HousekeeperAPI.js'
import { mapi } from '@/api/MAPI.js'
import GetActivationCode from '@/components/GetActivationCode'
import DeviceRFIDDetailDialog from './components/DeviceRFIDDetailDialog.vue'

export default {
  components: {
    FilterFrame,
    TableLoading,
    GetActivationCode,
    DeviceRFIDDetailDialog
  },
  data () {
    return {
      loading: false,
      rawTabletData: [], // 儲存從 API 取得的原始資料
      tabletList: [], // 處理後的戶別列表
      tagList: [],
      filter: {
        isDeviceBind: null,
        tag: null,
        isVirtual: null
      },
      tabletFilter: [],
      // 新增承租戶相關變數
      inputVirtualName: '', // 承租戶名稱 input
      isUpserting: false, // 用於標記是否正在新增 / 編輯承租戶，一次只能進行一筆、不可同時進行多筆
      isInvalid: false, // 用於標記輸入是否無效
      snapshotVirtualName: '' // 用於儲存承租戶名稱的快照，在新增編輯承租戶名稱時用於檢查是否有增修過
    }
  },
  computed: {
    isVirtualEnabled () {
      return this.$store.getters.isServiceEnabled(14) // 社區開啟承租戶功能
    }
  },
  async mounted () {
    await this.getTabletTags()
    this.setupFilterOptions()
    await this.getTabletList()
  },
  methods: {
    /**
     * 取得棟別列表
     */
    async getTabletTags () {
      try {
        const { data } = await housekeeperAPI.getCommunityTabletTag()
        this.tagList = data.Data.Tags
      } catch (err) {
        this.$message({
          message: this.$t('Household.Dashboard.Messages.GetBuildingTagsFailed'),
          type: 'warning'
        })
      }
    },

    /**
     * 設定過濾選項
     */
    setupFilterOptions () {
      const tabletFilter = [
        {
          label: this.$t('Household.Dashboard.Filters.ActivationStatus'),
          name: 'isDeviceBind',
          type: 'select',
          options: [
            { label: this.$t('Household.Dashboard.Filters.PleaseSelect'), value: '' },
            { label: this.$t('Household.Dashboard.Filters.Activated'), value: true },
            { label: this.$t('Household.Dashboard.Filters.NotActivated'), value: false }
          ],
          value: ''
        },
        {
          label: this.$t('Household.Dashboard.Filters.ByBuilding'),
          name: 'tag',
          type: 'select',
          options: [
            { label: this.$t('Household.Dashboard.Filters.PleaseSelect'), value: '' },
            ...this.tagList.map(item => ({
              label: item.Tag,
              value: item.Tag
            }))
          ],
          value: ''
        }
      ]
      if (this.isVirtualEnabled) {
        tabletFilter.push(
          {
            label: this.$t('Household.Dashboard.Filters.UnitType'),
            name: 'isVirtual',
            type: 'select',
            options: [
              { label: this.$t('Household.Dashboard.Filters.PleaseSelect'), value: '' },
              { label: this.$t('Household.Dashboard.Filters.Owner'), value: 0 },
              { label: this.$t('Household.Dashboard.Filters.Tenant'), value: 1 }
            ],
            value: ''
          }
        )
      }
      this.tabletFilter = tabletFilter
    },

    /**
     * 取得戶別列表（全部資料）
     */
    async getTabletList () {
      this.loading = true
      try {
        // 不帶任何篩選參數，取得全部資料
        const { data } = await housekeeperAPI.getCommunityTabletList({})

        // 儲存原始資料，用於前端篩選
        this.rawTabletData = data.Data.CommunityTablets || []

        // 初始化處理後的戶別列表（無篩選條件）
        this.tabletList = this.processTabletData([...this.rawTabletData])
      } catch (error) {
        console.error(error)
        this.$message({
          message: this.$t('Household.Dashboard.Messages.GetTabletListFailed'),
          type: 'warning'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * 處理戶別資料，並且整理成樹狀結構
     */
    processTabletData (tablets) {
      const newTablets = tablets.map(el => ({
        ...el,
        children: [],
        upsert: ''
      }))
      if (
        !this.isVirtualEnabled ||
        this.filter.isVirtual === 1
      ) {
        // 如果社區沒開承租戶功能或是僅篩選承租戶，則不需要樹狀結構
        // 為每個項目添加 index
        return newTablets.map((tablet, index) => ({
          ...tablet,
          children: null,
          index: index + 1
        }))
      }

      const childArr = newTablets.filter(el => el.ParentId) // 承租戶
      const fatherArr = newTablets.filter(el => !el.ParentId) // 區權戶

      // 把資料整理成element ui要的格式，已顯示樹狀table
      childArr.forEach(el => {
        const fatherIndex = fatherArr.findIndex(item => item.TabletId === el.ParentId)
        if (fatherIndex !== -1) {
          fatherArr[fatherIndex].children.push({
            ...el,
            index: '' // 所有 children 的 index 都設為 ''
          })
        } else {
          fatherArr.push({
            ...el,
            index: '' // 如果找不到 parent，也設為 ''
          })
        }
      })

      // 為 parent 項目設定 index，從 1 開始
      return fatherArr.map((father, index) => ({
        ...father,
        index: index + 1
      }))
    },

    /**
     * 搜尋功能（前端篩選）
     */
    search (searchParams) {
      this.filter = {
        isDeviceBind: searchParams.isDeviceBind,
        tag: searchParams.tag,
        isVirtual: searchParams.isVirtual
      }
      // 使用前端篩選更新 tabletList
      this.updateTabletList()
    },

    /**
     * 清除搜尋條件（前端篩選）
     */
    clearSearch () {
      this.filter = {
        isDeviceBind: null,
        tag: null,
        isVirtual: null
      }
      // 使用前端篩選更新 tabletList
      this.updateTabletList()
    },

    /**
     * 根據篩選條件更新 tabletList
     */
    updateTabletList () {
      const filteredData = this.getFilteredData()
      this.tabletList = this.processTabletData(filteredData)
    },

    /**
     * 根據篩選條件取得過濾後的資料
     */
    getFilteredData () {
      let filtered = [...this.rawTabletData]

      // 棟別篩選
      if (this.filter.tag) {
        filtered = filtered.filter(tablet => tablet.Tag === this.filter.tag)
      }

      // 戶別類型篩選
      if (this.filter.isVirtual === 1) {
        filtered = filtered.filter(tablet => tablet.IsVirtual === this.filter.isVirtual)
      }

      // 開通狀況篩選
      if (typeof this.filter.isDeviceBind === 'boolean') {
        if (!this.isVirtualEnabled || this.filter.isVirtual === 1) {
          // 社區無開承租戶或是僅篩選承租戶，直接 filter 即可
          filtered = filtered.filter(tablet => tablet.HasDeviceBound === this.filter.isDeviceBind)
        } else {
          // 區權戶 + 承租戶根據父層篩選
          // 只顯示已開通的戶別（包含其承租戶）
          filtered = filtered.filter(tablet => {
            if (!tablet.ParentId) {
              // 父層戶別，檢查自己是否開通
              return tablet.HasDeviceBound === this.filter.isDeviceBind
            } else {
              // 承租戶，檢查父層是否開通
              const parent = this.rawTabletData.find(p => p.TabletId === tablet.ParentId)
              return parent && parent.HasDeviceBound === this.filter.isDeviceBind
            }
          })
        }
      }
      return filtered
    },

    /**
     * 匯出住戶資料
     */
    async exportResidentData () {
      try {
        const res = await mapi.exportResident({
          isOpened: this.filter.isDeviceBind,
          tag: this.filter.tag,
          isVirtual: !!this.filter.isVirtual
        })
        const communityName = JSON.parse(localStorage.getItem('loginUserInfo')).CommunityName
        this.$downloadFile(res.data, `${communityName}_住戶資料表.xlsx`)
        this.$message({
          message: this.$t('Household.Dashboard.Messages.ExportSuccess'),
          type: 'success'
        })
      } catch (err) {
        console.error(err)
        this.$message({
          message: this.$t('Household.Dashboard.Messages.ExportFailed'),
          type: 'warning'
        })
      }
    },

    /**
     * 產生開通序號
     */
    generateActivationCode (tabletId, tabletNote) {
      this.$refs.getActivationCode.open(tabletId, tabletNote)
    },

    /**
     * 查詢裝置/載具明細
     */
    queryDeviceDetails (tabletId, tabletNote) {
      this.$refs.deviceRfidDetailDialog.open(tabletId, tabletNote)
    },

    /**
     * 租屋功能
     */
    async updateTabletIsRent (row) {
      try {
        // 取用原本的 IsRent 來 toggle
        const newIsRent = row.IsRent === 1 ? 0 : 1
        const rentData = { IsRent: newIsRent }

        // 呼叫 API 更新租屋狀態
        await housekeeperAPI.updateTabletIsRent(row.TabletId, rentData)

        // 更新本地資料源
        row.IsRent = newIsRent

        // 顯示成功訊息
        const message = newIsRent === 1 ? this.$t('Household.Dashboard.Messages.UpdateRentSuccessYes') : this.$t('Household.Dashboard.Messages.UpdateRentSuccessNo')
        this.$confirm(message, {
          showClose: false,
          showCancelButton: false,
          showConfirmButton: true,
          type: 'success'
        })
      } catch (err) {
        this.$alert(
          err?.response?.data?.ErrorMessage?.Description ||
            this.$t('Household.Dashboard.Messages.UpdateRentFailed'),
          this.$t('General.AlertTitle'),
          {
            showClose: false,
            showCancelButton: false,
            showConfirmButton: true,
            type: 'warning'
          })
      }
    },

    /**
     * 新增戶別（承租戶）
     * @param {Object} parentRow - 父層戶別資料
     */
    addVirtual (parentRow) {
      // 標注正在新增 / 編輯
      this.isUpserting = true
      // 清空新承租戶名稱
      this.inputVirtualName = ''
      this.snapshotVirtualName = ''

      // 直接異動原資料 tabletList 中的父層資料
      const parentIndex = this.tabletList.findIndex(item => item.TabletId === parentRow.TabletId)
      if (parentIndex === -1) return

      // 在子列表最上方新增一列用於輸入
      const newRow = {
        TabletId: `temp_${Date.now()}`, // 臨時 ID
        ParentId: parentRow.TabletId,
        index: '',
        HasDeviceBound: false, // 未開通
        Tag: this.tabletList[parentIndex].Tag, // 承襲父層棟別
        TabletNote: this.tabletList[parentIndex].TabletNote + '-', // 承襲父層棟別戶別
        IsVirtual: 1, // 承租戶
        DeviceCount: 0,
        PrivacyCount: 0,
        CarrierCount: 0,
        upsert: 'insert' // 標記為正在新增狀態
      }

      // 在子列表最前面插入新增列，直接操作 tabletList 中的資料
      this.tabletList[parentIndex].children.unshift(newRow)

      // 使用 $nextTick 確保 DOM 更新後再展開子層
      this.$nextTick(() => {
        // 使用 ref 取得表格元件實例
        if (this.$refs.table) {
          // 使用 element-ui 表格的 toggleRowExpansion 方法展開該行
          this.$refs.table.toggleRowExpansion(this.tabletList[parentIndex], true)
        }
      })
    },
    /**
     * 更新所有資料的 index
     */
    updateDataIndexes () {
      this.tabletList.forEach((item, index) => {
        // 更新 parent 的 index
        item.index = index + 1

        // 更新 children 的 index 為 ''
        if (item.children && item.children?.length > 0) {
          item.children.forEach(child => {
            child.index = ''
          })
        }
      })
    },

    /**
     * 排序變更處理
     * 因為期望 index 欄位不受排序影響，故此處自行實作排序邏輯
     */
    onSortChange ({ prop, order }) {
      if (!prop) return
      const direction = order === 'ascending' ? 1 : -1

      // 只對頂層資料進行排序，children 保持原有順序
      this.tabletList.sort((a, b) => {
        return this.$commonCompare(a[prop], b[prop]) * direction
      })

      // 排序後更新所有 index
      this.updateDataIndexes()
    },
    /**
     * 檢查 inputVirtualName 是否為空字串或僅包含空白字元。
     * 若為空，則將 isInvalid 設為 true，否則設為 false。
     * @returns {boolean} isInvalid - 是否為無效輸入。
     */
    checkIsInvalid () {
      return this.isInvalid = !this.inputVirtualName.trim()
    },
    /**
     * 檢查承租戶名稱重複
     * @param {Object} parent - 父層資料
     * @returns {boolean} 如果存在重複的承租戶名稱則返回 true，否則返回 false
     * @description 此方法會檢查父層資料之子項目中是否有其他項目使用相同的承租戶名稱
     */
    checkIsDuplicate (parent) {
      return !!parent.children.some(child =>
        !/temp_/.test(child.TabletId) && // 自己以外的兄弟姐妹
        child.VirtualName === this.inputVirtualName.trim() // 名字一樣
      )
    },
    /**
     * 確定新增承租戶
     * @param {Object} row - 正在編輯的行資料
     */
    async confirmAddVirtual (row) {
      // 檢查是否有輸入名稱
      if (this.checkIsInvalid()) {
        // 顯示 confirm 提示
        this.$confirm(
          this.$t('Household.Dashboard.Messages.PleaseEnterVirtualTabletName'),
          this.$t('General.AlertTitle'),
          {
            showClose: false,
            showCancelButton: false,
            showConfirmButton: true,
            type: 'warning'
          })
        return
      }

      try {
        const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
        // 檢查承租戶名稱重複
        if (this.checkIsDuplicate(this.tabletList[parentIndex])) {
          throw new Error('承租戶名稱重複')
        }

        // 呼叫 API 新增承租戶
        const virtualData = {
          VirtualName: this.inputVirtualName.trim()
        }
        const { data } = await housekeeperAPI.addVirtualTablet(row.ParentId, virtualData)

        // 成功後更新這一列的資料
        const updatedData = data.Data
        // 直接在 tabletList 中找到父層資料並更新
        const childIndex = this.tabletList[parentIndex].children.findIndex(item => item.TabletId === row.TabletId)
        const child = this.tabletList[parentIndex].children[childIndex]
        // 使用 API 回傳的資料更新這一列，並清除編輯狀態
        this.$set(this.tabletList[parentIndex].children, childIndex, {
          ...child,
          ...updatedData,
          upsert: '' // 清除編輯狀態
        })

        // 顯示成功訊息
        this.$confirm(this.$t('Household.Dashboard.Messages.AddVirtualTabletSuccess'), {
          showClose: false,
          showCancelButton: false,
          showConfirmButton: true,
          type: 'success'
        })

        // 清理狀態
        this.inputVirtualName = ''
        this.isUpserting = false
        this.isInvalid = false
      } catch (err) {
        if (err?.message === '承租戶名稱重複' || err?.response?.data?.ErrorMessage?.Code === 400001) {
          this.$confirm(
            this.$t('Household.Dashboard.Messages.DuplicateVirtualTabletName'),
            this.$t('General.AlertTitle'),
            {
              showClose: false,
              showCancelButton: false,
              showConfirmButton: true,
              type: 'warning'
            })
          return
        }
        this.$confirm(
          err?.response?.data?.ErrorMessage?.Description ||
            this.$t('Household.Dashboard.Messages.AddVirtualTabletFailed'),
          this.$t('General.AlertTitle'),
          {
            showClose: false,
            showCancelButton: false,
            showConfirmButton: true,
            type: 'warning'
          })
      }
    },
    /**
     * 取消新增承租戶
     * @param {Object} row - 正在編輯的行資料
     */
    async cancelAddVirtual (row) {
      const realCancel = () => {
        // 直接在 tabletList 中找到父層資料並更新
        const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
        this.tabletList[parentIndex].children.splice(0, 1)

        // 清理狀態
        this.isUpserting = false
        this.isInvalid = false
      }
      if (this.inputVirtualName !== this.snapshotVirtualName) {
        const confirmResult = await this.$confirm(
          this.$t('Household.Dashboard.Messages.NameNotSaved', { tabletNote: row.TabletNote }),
          this.$t('General.AlertTitle'),
          {
            confirmButtonText: this.$t('General.OK'),
            cancelButtonText: this.$t('General.Cancel'),
            type: 'warning'
          }
        )
        if (confirmResult === 'confirm') {
          realCancel()
        }
      } else {
        realCancel()
      }
    },

    /**
     * 取得基本戶別名稱（TabletNote 減去 VirtualName）
     * @param {Object} row - 承租戶資料
     * @returns {string} - 基本戶別名稱
     */
    getBaseTabletNote (row) {
      if (!row.VirtualName || !row.TabletNote) {
        return row.TabletNote || ''
      }
      // 移除 VirtualName 部分，通常是在最後面
      return row.TabletNote.replace(row.VirtualName, '').trim()
    },

    /**
     * 開始編輯承租戶
     * @param {Object} row - 要編輯的承租戶資料
     */
    editVirtual (row) {
      // 設定編輯狀態
      this.isUpserting = true
      const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
      const childIndex = this.tabletList[parentIndex].children.findIndex(item => item.TabletId === row.TabletId)
      this.$set(this.tabletList[parentIndex].children[childIndex], 'upsert', 'update')

      // 預填 VirtualName
      this.inputVirtualName = row.VirtualName || ''
      this.snapshotVirtualName = row.VirtualName || ''
      this.isInvalid = false
    },

    /**
     * 確認編輯承租戶
     * @param {Object} row - 編輯中的承租戶資料
     */
    async confirmEditVirtual (row) {
      // 檢查輸入是否有效
      if (this.checkIsInvalid()) {
        this.$message({
          message: this.$t('Household.Dashboard.Messages.PleaseEnterVirtualTabletName'),
          type: 'warning'
        })
        return
      }

      const showEditSuccess = () => {
        // 顯示成功訊息
        this.$alert(this.$t('Household.Dashboard.Messages.UpdateTenantNameSuccess'), {
          showClose: false,
          showCancelButton: false,
          showConfirmButton: true,
          confirmButtonText: this.$t('General.OK'),
          type: 'success'
        })
      }

      if (row.VirtualName === this.inputVirtualName.trim()) {
        showEditSuccess()
        // 如果名稱沒有變更，直接取消編輯
        this.cancelEditVirtual(row)
        return
      }

      try {
        const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
        // 檢查承租戶名稱重複
        if (this.checkIsDuplicate(this.tabletList[parentIndex])) {
          throw new Error('承租戶名稱重複')
        }

        // 準備更新資料
        const renameData = {
          VirtualName: this.inputVirtualName.trim()
        }

        // 呼叫 API
        const { data } = await housekeeperAPI.renameVirtualTablet(row.TabletId, renameData)
        const newData = data.Data

        // 更新本地資料
        // 尋找父層與子層索引
        if (parentIndex === -1) return // 找不到父層直接返回

        const childIndex = this.tabletList[parentIndex].children.findIndex(item => item.TabletId === row.TabletId)
        if (childIndex === -1) return // 找不到子層直接返回

        // 只更新 VirtualName 與 TabletNote，保留其他欄位
        this.$set(this.tabletList[parentIndex].children, childIndex, {
          ...this.tabletList[parentIndex].children[childIndex],
          ...newData,
          upsert: ''
        })

        showEditSuccess()
        this.isUpserting = false
        this.isInvalid = false
      } catch (err) {
        if (err?.message === '承租戶名稱重複' || err?.response?.data?.ErrorMessage?.Code === 400001) {
          this.$confirm(
            this.$t('Household.Dashboard.Messages.DuplicateVirtualTabletName'),
            this.$t('General.AlertTitle'),
            {
              showClose: false,
              showCancelButton: false,
              showConfirmButton: true,
              type: 'warning'
            })
          return
        }
        this.$confirm(
          err?.response?.data?.ErrorMessage?.Description ||
            this.$t('Household.Dashboard.Messages.UpdateTenantNameFailed'),
          {
            showClose: false,
            showCancelButton: false,
            showConfirmButton: true,
            type: 'warning'
          })
      } finally {
        this.loading = false
      }
    },

    /**
     * 取消編輯承租戶
     * @param {Object} row - 編輯中的承租戶資料
     */
    async cancelEditVirtual (row) {
      const realCancel = () => {
        // 關閉編輯模式
        this.isUpserting = false
        this.isInvalid = false

        // 更新本地資料
        const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
        const childIndex = this.tabletList[parentIndex].children.findIndex(item => item.TabletId === row.TabletId)
        this.tabletList[parentIndex].children[childIndex].upsert = null
      }
      if (this.inputVirtualName !== this.snapshotVirtualName) {
        const confirmResult = await this.$confirm(
          this.$t('Household.Dashboard.Messages.NameNotSaved', { tabletNote: row.TabletNote }),
          this.$t('General.AlertTitle'),
          {
            confirmButtonText: this.$t('General.OK'),
            cancelButtonText: this.$t('General.Cancel'),
            type: 'warning'
          }
        )
        if (confirmResult === 'confirm') {
          realCancel()
        }
      } else {
        realCancel()
      }
    },

    /**
     * 刪除承租戶
     * @param {Object} row - 要刪除的承租戶資料
     */
    async deleteVirtual (row) {
      // 檢查是否有裝置或載具
      if (row.DeviceCount > 0 || row.CarrierCount > 0) {
        await this.$alert(
          this.$t('Household.Dashboard.Messages.DeleteVirtualTabletBlocked', { tabletNote: row.TabletNote }),
          this.$t('General.AlertTitle'),
          {
            confirmButtonText: this.$t('General.OK'),
            type: 'warning',
            showClose: false
          }
        )

        // 取得父層資料，開啟裝置、載具彈窗
        const parent = this.rawTabletData.find(item => item.TabletId === row.ParentId)
        if (parent) {
          await this.$refs.deviceRfidDetailDialog.open(parent.TabletId, parent.TabletNote)
        }

        return
      }

      try {
        // 顯示確認刪除的 Alert
        const confirmResult = await this.$confirm(
          this.$t('Household.Dashboard.Messages.ConfirmDeleteVirtualTablet', { tabletNote: row.TabletNote }),
          this.$t('General.AlertTitle'),
          {
            confirmButtonText: this.$t('General.OK'),
            cancelButtonText: this.$t('General.Cancel'),
            type: 'warning'
          }
        )

        if (confirmResult === 'confirm') {
          try {
            await housekeeperAPI.deleteVirtualTablet(row.TabletId)

            // 從本地資料中移除該筆資料
            const parentIndex = this.tabletList.findIndex(item => item.TabletId === row.ParentId)
            const childIndex = this.tabletList[parentIndex].children.findIndex(item => item.TabletId === row.TabletId)
            this.tabletList[parentIndex].children.splice(childIndex, 1)

            // 顯示成功訊息
            this.$confirm(this.$t('Household.Dashboard.Messages.DeleteVirtualTabletSuccess', { tabletNote: row.TabletNote }), {
              showClose: false,
              showCancelButton: false,
              showConfirmButton: true,
              confirmButtonText: this.$t('General.OK'),
              type: 'success'
            })
          } catch (err) {
            // 顯示錯誤訊息
            const errorMessage = err?.response?.data?.ErrorMessage?.Description || this.$t('Household.Dashboard.Messages.DeleteVirtualTabletFailed')
            this.$confirm(errorMessage, {
              showClose: false,
              showCancelButton: false,
              showConfirmButton: true,
              confirmButtonText: this.$t('General.OK'),
              type: 'warning'
            })
          } finally {
            this.loading = false
          }
        }
      } catch (err) {
        // 使用者取消刪除，不需要處理
        console.error(err)
      }
    },
    unbindSuccess ({
      tabletId,
      type
    }) {
      // 將原始資料也更新，使排序時也能用新資料排序
      const flatTarget = this.rawTabletData.find(tablet => tablet.TabletId === tabletId)
      flatTarget.DeviceCount -= type === 'device' ? 1 : 0
      flatTarget.CarrierCount -= type === 'carrier' ? 1 : 0

      // 將呈現用的資料也進行更新
      let treeTarget
      if (this.isVirtualEnabled && flatTarget.ParentId) {
        // 有承租戶功能且為承租戶，先尋找父層，再尋找子層
        const parent = this.tabletList.find(tablet => tablet.TabletId === flatTarget.ParentId)
        treeTarget = parent.children.find(tablet => tablet.TabletId === tabletId)
      } else {
        treeTarget = this.tabletList.find(tablet => tablet.TabletId === tabletId)
      }
      treeTarget.DeviceCount -= type === 'device' ? 1 : 0
      treeTarget.CarrierCount -= type === 'carrier' ? 1 : 0
    }
  }
}
</script>

<style lang="scss" scoped>
#tabletDashboard{
  @include mainLayer();
  ::v-deep .el-table {
    @include basicTable();
    .is-rent{
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      button{
        margin: 0px;
      }
    }
    .upsert-virtual-name{
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      .el-input {
        width: 100%;
        &.invalid {
          .el-input__inner {
            border: 1px solid $dangerColor;
          }
        }
      }
    }
  }
}

.center-block {
  display: flex;
  align-items: center;
  margin: 20px 0;
  padding: 15px;
  border-radius: 4px;
}

.text-opened {
  color: $mainColor;
  font-weight: bold;
}

.text-notOpened {
  color: $dangerColor;
  font-weight: bold;
}

.privacy-count {
  color: $dangerColor;
  font-weight: bold;
}

.detail-link {
  color: #409eff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.newslist {
  margin-top: 20px;
}
</style>
